import type { Dispatch, RefObject, SetStateAction } from "react";
import type { FernieDictationNotice, SpeechRecognitionLike } from "~/helpers/aiChat/fernieDictation";

import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import activeLocaleTag from "~/helpers/activeLocale";
import fernieDictation from "~/helpers/aiChat/fernieDictation";
import logger from "~/helpers/logger";
import useFeatureFlag from "~/hooks/useFeatureFlag";

type FernieDictationPhase = "idle" | "recording" | "transcribing";

interface UseFernieDictationOptions {
	inputRef: RefObject<HTMLInputElement | null>;
	setDraft: Dispatch<SetStateAction<string>>;
}

export interface UseFernieDictationReturn {
	isAvailable: boolean;
	notice: FernieDictationNotice | null;
	phase: FernieDictationPhase;
	cancel: () => void;
	dismissNotice: () => void;
	start: () => void;
	stop: () => void;
}

interface DictationSession {
	captureTimer?: ReturnType<typeof setTimeout>;
	failureNotice: FernieDictationNotice | null;
	hasReachedLimit: boolean;
	heardPhrases: string[];
	recognition: SpeechRecognitionLike;
	transcribeTimer?: ReturnType<typeof setTimeout>;
}

const forgetSession = (sessionRef: RefObject<DictationSession | null>, session: DictationSession) => {
	clearTimeout(session.captureTimer);
	clearTimeout(session.transcribeTimer);
	sessionRef.current = null;
};

const abandonSession = (sessionRef: RefObject<DictationSession | null>): boolean => {
	const session = sessionRef.current;
	if (!session) return false;
	forgetSession(sessionRef, session);
	session.recognition.abort();
	return true;
};

const useFernieDictation = ({ inputRef, setDraft }: UseFernieDictationOptions): UseFernieDictationReturn => {
	const { config, isEnabled: isAiChatEnabled } = useFeatureFlag(FeatureFlagName.AiChat);
	const { pathname } = useLocation();
	const [phase, setPhase] = useState<FernieDictationPhase>("idle");
	const [notice, setNotice] = useState<FernieDictationNotice | null>(null);
	const sessionRef = useRef<DictationSession | null>(null);

	const isEnabled = isAiChatEnabled && fernieDictation.isDictationEnabled(config);
	const isAvailable = isEnabled && fernieDictation.resolveSpeechRecognition() !== null;

	useEffect(
		() => () => {
			abandonSession(sessionRef);
		},
		[]
	);

	useEffect(() => {
		if (abandonSession(sessionRef)) setPhase("idle");
	}, [pathname]);

	useEffect(() => {
		if (!isEnabled && abandonSession(sessionRef)) setPhase("idle");
	}, [isEnabled]);

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === "hidden" && abandonSession(sessionRef)) setPhase("idle");
		};
		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
	}, []);

	const finishSession = (session: DictationSession) => {
		if (sessionRef.current !== session) return;
		const dictatedText = session.heardPhrases.reduce(fernieDictation.appendDictatedText, "");
		forgetSession(sessionRef, session);
		setPhase("idle");
		if (dictatedText) {
			setDraft((draft) => fernieDictation.appendDictatedText(draft, dictatedText));
			inputRef.current?.focus();
		}
		if (session.failureNotice) setNotice(session.failureNotice);
		else if (!dictatedText) setNotice("silence");
		else if (session.hasReachedLimit) setNotice("limit");
	};

	const stopSession = (session: DictationSession) => {
		clearTimeout(session.captureTimer);
		session.transcribeTimer = setTimeout(() => {
			abandonSession(sessionRef);
			setPhase("idle");
			setNotice("failed");
		}, fernieDictation.TRANSCRIBE_TIMEOUT_MS);
		setPhase("transcribing");
		session.recognition.stop();
	};

	const start = () => {
		const Recognition = fernieDictation.resolveSpeechRecognition();
		if (!Recognition) return;
		setNotice(null);

		const recognition = new Recognition();
		recognition.lang = activeLocaleTag();
		recognition.continuous = true;

		const session: DictationSession = { failureNotice: null, hasReachedLimit: false, heardPhrases: [], recognition };
		recognition.onresult = (event) => {
			for (let index = event.resultIndex; index < event.results.length; index++) {
				session.heardPhrases.push(event.results[index][0].transcript);
			}
		};
		recognition.onerror = (event) => {
			session.failureNotice = fernieDictation.mapRecognitionErrorToNotice(event.error);
		};
		recognition.onend = () => finishSession(session);

		try {
			recognition.start();
		} catch (error: unknown) {
			logger.warn("fernie dictation could not start", error);
			setNotice("failed");
			return;
		}
		sessionRef.current = session;
		session.captureTimer = setTimeout(() => {
			session.hasReachedLimit = true;
			stopSession(session);
		}, fernieDictation.MAX_CAPTURE_MS);
		setPhase("recording");
	};

	const stop = () => {
		if (sessionRef.current) stopSession(sessionRef.current);
	};

	const cancel = () => {
		if (abandonSession(sessionRef)) setPhase("idle");
	};

	const dismissNotice = () => setNotice(null);

	return { cancel, dismissNotice, isAvailable, notice, phase, start, stop };
};

export default useFernieDictation;
